/* The provisioning screen — **B-23**. Forms that create structure, so the rules it has to carry
   are about consequence rather than density: a refusal is read, a hint says what a control will
   do before it is pressed, and nothing is pre-selected that a customer should choose. */

.list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 8px;
}

.row {
  display: grid;
  gap: 2px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel-2);
}

.name {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

.meta {
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.note {
  margin: 10px 0 0;
  font-size: var(--t-min);
  line-height: 1.6;
  color: var(--ink-2);
}

/* A form is a grid of labelled fields rather than a row of bare inputs: every field here decides
   something a figure inherits, and a placeholder is not a label. */
.form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px;
  /* **`start`, not `end`.** With `end`, a taller field (the year end is two controls and a hint)
     drags the whole row down and the shorter ones sit under it — so the first thing a reader saw
     was an unlabelled gap above "Registered name". Fields align from the top and read in order. */
  align-items: start;
  margin: 14px 0 0;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
}

.field {
  display: grid;
  gap: 4px;
}

.label {
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink);
}

.input {
  font: inherit;
  font-size: var(--t-dense);
  padding: 7px 9px;
  border: 1px solid var(--line-2);
  border-radius: 6px;
  background: var(--panel);
  color: var(--ink);
}

.input:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 1px;
}

.check {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
  align-items: start;
  font-size: var(--t-dense);
  color: var(--ink);
}

/* What a control will do, beside the control. */
.hint {
  display: block;
  margin-top: 3px;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
  font-weight: 400;
}

.submit {
  /* Pushed to the baseline of its own cell so it does not float beside a label. */
  align-self: end;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 600;
  padding: 8px 14px;
  border: 0;
  border-radius: 6px;
  background: var(--accent);
  color: var(--on-fill);
  cursor: pointer;
}

.submit:hover {
  background: var(--accent-h);
}

.submit:disabled {
  border-color: var(--btn-off-bg);
  background: var(--btn-off-bg);
  color: var(--btn-off-ink);
  cursor: default;
}

/* Retiring is not destructive and must not look it — there is no delete here at all. It is
   `--warn` rather than `--crit` because nothing is lost. */
.retire {
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 600;
  padding: 8px 14px;
  border: 1px solid var(--warn-line);
  border-radius: 6px;
  background: var(--warn-w);
  color: var(--warn);
  cursor: pointer;
}

.inlineButton {
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: var(--t-min);
  color: var(--accent);
  cursor: pointer;
  text-decoration: underline;
  justify-self: start;
}

.sub {
  margin-top: 10px;
  padding-left: 12px;
  border-left: 2px solid var(--line-2);
}

.meters {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 6px;
}

.meter {
  display: grid;
  gap: 1px;
  font-size: var(--t-dense);
  color: var(--ink);
}

/* **Full width, both of them.** The screen's whole argument is that the server's refusal is
   written to be read — *"Sierravale Foods Ltd is already the parent of this group"* — and these
   sat in column 1 of the twelve-column grid, rendering that sentence one word per line. A message
   nobody can read is the same as no message. */
.saved {
  grid-column: 1 / 13;
  margin: 0 0 10px;
  padding: 9px 12px;
  border: 1px solid var(--ok-line);
  border-radius: var(--radius);
  background: var(--ok-w);
  color: var(--ok);
  font-size: var(--t-dense);
}

/* The server's own sentence. It is written to be read, so it is given room to be. */
.failed {
  grid-column: 1 / 13;
  margin: 0 0 10px;
  padding: 9px 12px;
  border: 1px solid var(--crit-line);
  border-radius: var(--radius);
  background: var(--crit-w);
  color: var(--crit);
  font-size: var(--t-dense);
  line-height: 1.55;
}

/* Two controls that are one fact — a day and a month make a year end, and splitting them across
   the form grid would read as two unrelated questions. */
.pair {
  display: grid;
  grid-template-columns: 5rem 1fr;
  gap: 6px;
}

/* One box of a group that has a caption of its own (the address): a small visible label above the
   input, so the narrow ones are never four unnamed rectangles (G-Wadmin-08). */
.addressBox {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.subLabel {
  font-size: var(--t-min);
  font-weight: 500;
  color: var(--ink-2);
}

/* The town, the county or state and the postal code on one line: the postal code is the short one. */
.addressRow {
  display: grid;
  grid-template-columns: 1fr 1fr 7rem;
  gap: 6px;
}

/* A site whose lease treatment nobody has stated (ADR-0034). `--warn`, not `--crit`: an unanswered
   site is a question outstanding, not a mistake — and the person reading this list is usually the
   one who has to go and ask an accountant. */
.unanswered {
  color: var(--warn);
  font-weight: 600;
}

/* Cancel on an always-open form: a repair rather than a decision, so it reads as a link and is a
   real `<button>`. Same treatment as `Panel.module.css`'s retry, because they are the same kind of
   control. */
.cancel {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: var(--t-min);
  color: var(--accent);
  cursor: pointer;
  text-decoration: underline;
}

/* The submit and its cancel, side by side and baseline-aligned. Its own class rather than `.pair`,
   which is a 5rem/1fr grid for a day-and-month field and laid a cancel out as a second column of
   a form row. */
.buttons {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* A meter that has come out. `--warn` for `.unanswered`'s reason rather than `--crit`: nothing is
   lost and nothing is wrong — the supply point is gone and its readings are still the reason last
   year's figures exist. It is marked at all because "retired 31 March 2026" in the same ink as the
   unit reads as part of the unit. */
.retired {
  color: var(--warn);
}

/* An edge out of this register (EXP-06). A link rather than a button, because it goes somewhere —
   `.inlineButton` is the control that opens something here, and the two must not look alike. */
.crossLink {
  color: var(--accent);
}
